/**
 * Billing review > All entries: released time across the firm, filtered by
 * timekeeper, client, fee and status, with bulk approve, return and coding.
 * The Type switch shows the firm's case expenses instead.
 */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type RowSelectionState
} from "@tanstack/react-table";
import { Check, Search, Undo2 } from "lucide-react";
import { useEffect, useMemo, useState, type FC, type ReactNode } from "react";
import { useDebounce } from "use-debounce";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { reviewActions } from "../actions";
import { CaseDot } from "../components/CasePicker";
import { CodeSelect } from "../components/CodeSelect";
import { useMatters, useReviewEntries, useTimekeepers } from "../queries";
import { Segmented } from "../settings/SettingRow";
import type { FeeArrangement, ReviewKpis, TimeEntry } from "../types";
import { useHeld } from "../useHeld";
import { formatHours, totalHours } from "../utils";
import { ExpenseReview } from "./ExpenseReview";
import { ALL, FilterSelect } from "./FilterSelect";
import { ReturnDialog } from "./ReturnDialog";
import { ReviewEntrySheet } from "./ReviewEntrySheet";
import {
  amountLabel,
  canApprove,
  isSelectable,
  statusesFor,
  type EntryType,
  type StatusFilter
} from "./reviewModel";
import { ReviewStatus } from "./ReviewStatus";

const FEES: ReadonlyArray<{ id: FeeArrangement; label: string }> = [
  { id: "hourly", label: "Hourly" },
  { id: "flat_fee", label: "Flat fee" },
  { id: "hourly_capped", label: "Hourly, capped" },
  { id: "non_billable", label: "Non-billable" }
];

const shortDate = (iso: string): string => {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const column = createColumnHelper<TimeEntry>();

type Props = {
  range: { from?: string; to?: string };
  kpis?: ReviewKpis;
};

const TYPES: ReadonlyArray<{ id: EntryType; label: string }> = [
  { id: "time", label: "Time" },
  { id: "expense", label: "Expenses" }
];

const TimeEntries: FC<Props & { typeSwitch: ReactNode }> = ({
  range,
  kpis,
  typeSwitch
}) => {
  const [status, setStatus] = useState<StatusFilter>("all");
  const [user, setUser] = useState(ALL);
  const [client, setClient] = useState(ALL);
  const [fee, setFee] = useState(ALL);
  const [search, setSearch] = useState("");
  const [q] = useDebounce(search.trim(), 300);
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [returning, setReturning] = useState<TimeEntry[] | null>(null);
  const returnShown = useHeld(returning);

  const filters = useMemo(
    () => ({
      status: statusesFor(status),
      from: range.from,
      to: range.to,
      user: user === ALL ? undefined : user,
      client: client === ALL ? undefined : client,
      fee: fee === ALL ? undefined : (fee as FeeArrangement),
      q: q || undefined
    }),
    [status, range.from, range.to, user, client, fee, q]
  );
  const query = useReviewEntries(filters);
  const keepers = useTimekeepers();
  const matters = useMatters();
  const entries = useMemo(() => query.data?.entries ?? [], [query.data]);

  useEffect(() => {
    const ids = new Set(entries.filter(isSelectable).map((e) => e.id));
    setSelection((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([id, on]) => on && ids.has(id))
      );
      return Object.keys(next).length === Object.keys(prev).length
        ? prev
        : next;
    });
  }, [entries]);

  const columns = useMemo(
    () => [
      column.display({
        id: "select",
        header: ({ table }) => (
          <Checkbox
            aria-label="Select all"
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                ? "indeterminate"
                : false
            }
            disabled={!table.getRowModel().rows.some((r) => r.getCanSelect())}
            onCheckedChange={(on) => table.toggleAllRowsSelected(Boolean(on))}
          />
        ),
        cell: ({ row }) =>
          row.getCanSelect() ? (
            <Checkbox
              aria-label={`Select ${row.original.case_label || "entry"}`}
              checked={row.getIsSelected()}
              onClick={(e) => e.stopPropagation()}
              onCheckedChange={(on) => row.toggleSelected(Boolean(on))}
            />
          ) : null
      }),
      column.accessor("work_date", {
        header: "Date",
        cell: (c) => (
          <span className="text-fg-3 tabular-nums">
            {shortDate(c.getValue())}
          </span>
        )
      }),
      column.accessor((e) => e.user_name ?? "", {
        id: "timekeeper",
        header: "Timekeeper",
        cell: (c) => <span className="block truncate">{c.getValue()}</span>
      }),
      column.accessor("case_label", {
        header: "Case",
        cell: (c) => (
          <span className="flex min-w-0 items-center gap-1.5">
            <CaseDot id={c.row.original.individual_id} />
            <span className="truncate">{c.getValue() || "No case"}</span>
          </span>
        )
      }),
      column.accessor("narrative", {
        header: "Narrative",
        cell: (c) => <span className="block truncate">{c.getValue()}</span>
      }),
      column.accessor((e) => e.utbms_activity ?? "", {
        id: "code",
        header: "Code",
        cell: (c) => (
          <span className="font-mono text-sm text-fg-3">{c.getValue()}</span>
        )
      }),
      column.accessor("duration_hours", {
        header: () => <span className="block text-right">Hours</span>,
        cell: (c) => (
          <span className="block text-right font-medium tabular-nums">
            {formatHours(c.getValue())}
          </span>
        )
      }),
      column.display({
        id: "amount",
        header: () => <span className="block text-right">Amount</span>,
        cell: ({ row }) => {
          const label = amountLabel(row.original);
          return (
            <span
              className={cn(
                "block text-right tabular-nums",
                label.startsWith("$") ? "" : "text-fg-3"
              )}
            >
              {label}
            </span>
          );
        }
      }),
      column.accessor("status", {
        header: "Status",
        cell: (c) => <ReviewStatus entry={c.row.original} />
      })
    ],
    []
  );

  const table = useReactTable({
    data: entries,
    columns,
    getRowId: (e) => e.id,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) => isSelectable(row.original),
    state: { rowSelection: selection },
    onRowSelectionChange: setSelection
  });

  const selected = entries.filter((e) => selection[e.id]);
  const approvable = selected.filter(canApprove);
  const opened = entries.find((e) => e.id === openId) ?? null;
  const returnTo = (rows: TimeEntry[]) => {
    const names = new Set(rows.map((e) => e.user_name ?? ""));
    return names.size === 1 ? [...names][0].split(" ")[0] || null : null;
  };

  const keeperOptions = (keepers.data?.timekeepers ?? []).map((k) => ({
    id: k.user_id || k.uid,
    label: k.name || k.email || k.user_id
  }));
  const clientOptions = (matters.data ?? []).map((m) => ({
    id: m.individual_id,
    label: m.label
  }));
  const awaiting = kpis?.awaiting_approval.count;

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="review-entries">
      <div className="flex flex-wrap items-center gap-1.5 px-4 py-2.5">
        {typeSwitch}
        <span className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-fg-3" />
          <Input
            aria-label="Search narratives"
            placeholder="Search narratives"
            className="h-7 w-56 pl-7 text-sm"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </span>
        <FilterSelect
          label="Timekeeper"
          value={user}
          options={keeperOptions}
          onChange={setUser}
        />
        <FilterSelect
          label="Client"
          value={client}
          options={clientOptions}
          onChange={setClient}
        />
        <FilterSelect
          label="Fee"
          value={fee}
          options={FEES}
          onChange={setFee}
        />
        <span className="flex-1" />
        <Segmented
          label="Status"
          value={status}
          onChange={setStatus}
          options={[
            { id: "all", label: "All" },
            {
              id: "released",
              label: awaiting ? `Released ${awaiting}` : "Released"
            },
            { id: "approved", label: "Approved" },
            { id: "exported", label: "Exported" },
            { id: "returned", label: "Returned" }
          ]}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        {selected.length ? (
          <div
            className="sticky top-0 z-10 mb-2 flex h-10 items-center gap-2.5 rounded-xl bg-foreground pr-1.5 pl-3.5 text-background"
            data-testid="review-bulk-bar"
          >
            <span className="text-base font-medium tabular-nums">
              {selected.length} selected · {formatHours(totalHours(selected))}
            </span>
            <span className="flex-1" />
            <span className="[&_button]:h-7 [&_button]:w-44 [&_button]:border-background/20 [&_button]:bg-transparent [&_button]:text-background">
              <CodeSelect
                onChange={(code) =>
                  void reviewActions.bulkEdit(
                    selected.map((e) => e.id),
                    { utbms_activity: code }
                  )
                }
              />
            </span>
            <Button
              size="xs"
              variant="ghost"
              className="text-background hover:bg-background/10 hover:text-background"
              onClick={() => setReturning(selected)}
            >
              <Undo2 /> Return
            </Button>
            <Button
              size="xs"
              variant="secondary"
              disabled={!approvable.length}
              onClick={async () => {
                const out = await reviewActions.approve(
                  approvable.map((e) => e.id)
                );
                if (out) setSelection({});
              }}
            >
              <Check /> Approve
              {approvable.length !== selected.length && approvable.length
                ? ` ${approvable.length}`
                : ""}
            </Button>
          </div>
        ) : null}

        {query.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : entries.length ? (
          <div className="shrink-0 overflow-hidden rounded-xl border border-line-2">
            <Table className="table-fixed text-base">
              <colgroup>
                <col className="w-9" />
                <col className="w-16" />
                <col className="w-32" />
                <col className="w-48" />
                <col />
                <col className="w-14" />
                <col className="w-14" />
                <col className="w-24" />
                <col className="w-24" />
              </colgroup>
              <TableHeader className="bg-fill-1">
                {table.getHeaderGroups().map((group) => (
                  <TableRow key={group.id}>
                    {group.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className="h-8 text-sm font-medium text-fg-3"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-testid="review-row"
                    data-state={
                      openId === row.id || row.getIsSelected()
                        ? "selected"
                        : undefined
                    }
                    className="h-10.5 cursor-pointer"
                    onClick={() => setOpenId(row.id)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-1.5">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <Empty className="border border-dashed" data-testid="review-empty">
            <EmptyHeader>
              <EmptyTitle>Nothing here</EmptyTitle>
              <EmptyDescription>
                {status === "all" && !q && user === ALL && client === ALL
                  ? "Entries show up here once timekeepers release them."
                  : "No entries match these filters."}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </div>

      <ReviewEntrySheet
        row={opened}
        onClose={() => setOpenId(null)}
        onReturn={(entry) => setReturning([entry])}
      />
      <ReturnDialog
        open={Boolean(returning)}
        count={returnShown?.length ?? 0}
        to={returnShown ? returnTo(returnShown) : null}
        onOpenChange={(open) => !open && setReturning(null)}
        onReturn={async (note) => {
          if (!returning) return;
          const out = await reviewActions.returnEntries(
            returning.map((e) => e.id),
            note
          );
          if (out) {
            setSelection({});
            setOpenId(null);
          }
        }}
      />
    </div>
  );
};

export const EntriesTab: FC<Props> = ({ range, kpis }) => {
  const [type, setType] = useState<EntryType>("time");
  const typeSwitch = (
    <Segmented label="Type" value={type} options={TYPES} onChange={setType} />
  );
  return type === "expense" ? (
    <ExpenseReview range={range} typeSwitch={typeSwitch} />
  ) : (
    <TimeEntries range={range} kpis={kpis} typeSwitch={typeSwitch} />
  );
};
